if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, true));
      });
      if (this.dataset.desktopLayout === 'thumbnail_slider' && this.mql.matches) this.removeListSemantic();
      // TAQB START: desktop gallery arrows with thumbnails — use setActiveMedia, not slider scroll
      this.setupDesktopGalleryArrowsNavigation();
      // TAQB END
    }

    // TAQB START: desktop gallery arrows with thumbnails visible
    usesDesktopGalleryArrowNavigation() {
      const productRoot = this.closest('.product');
      return (
        this.dataset.desktopLayout === 'thumbnail_slider' &&
        this.mql.matches &&
        productRoot?.classList.contains('product--show-gallery-arrows-desktop') &&
        !productRoot?.classList.contains('product--hide-thumbnails') &&
        this.elements.thumbnails
      );
    }

    getNavigableMediaSlides() {
      return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')).filter((slide) => {
        if (slide.classList.contains('product__media-item--variant')) return false;
        if (slide.hasAttribute('data-gang-option') && !slide.classList.contains('gang__active')) return false;
        return true;
      });
    }

    setupDesktopGalleryArrowsNavigation() {
      const prevButton = this.elements.viewer?.querySelector('button[name="previous"]');
      const nextButton = this.elements.viewer?.querySelector('button[name="next"]');
      if (!prevButton || !nextButton) return;

      this.onDesktopGalleryArrowClick = (event) => {
        if (!this.usesDesktopGalleryArrowNavigation()) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const slides = this.getNavigableMediaSlides();
        if (slides.length < 2) return;

        const active = slides.find((slide) => slide.classList.contains('is-active')) || slides[0];
        let index = slides.indexOf(active);
        if (index === -1) index = 0;

        const delta = event.currentTarget.name === 'next' ? 1 : -1;
        const nextIndex = Math.max(0, Math.min(slides.length - 1, index + delta));
        if (nextIndex === index) return;

        this.setActiveMedia(slides[nextIndex].dataset.mediaId, false, true);
      };

      prevButton.addEventListener('click', this.onDesktopGalleryArrowClick, true);
      nextButton.addEventListener('click', this.onDesktopGalleryArrowClick, true);
      this.mql.addEventListener('change', () => this.updateDesktopGalleryArrowControls());
      this.updateDesktopGalleryArrowControls();
    }

    updateDesktopGalleryArrowControls() {
      const prevButton = this.elements.viewer?.querySelector('button[name="previous"]');
      const nextButton = this.elements.viewer?.querySelector('button[name="next"]');
      if (!prevButton || !nextButton) return;

      if (!this.usesDesktopGalleryArrowNavigation()) {
        if (typeof this.elements.viewer?.update === 'function') {
          this.elements.viewer.update();
        }
        return;
      }

      const slides = this.getNavigableMediaSlides();
      const active = slides.find((slide) => slide.classList.contains('is-active')) || slides[0];
      const index = Math.max(0, slides.indexOf(active));

      const currentPageElement = this.elements.viewer.querySelector('.slider-counter--current');
      const pageTotalElement = this.elements.viewer.querySelector('.slider-counter--total');
      if (currentPageElement) currentPageElement.textContent = index + 1;
      if (pageTotalElement) pageTotalElement.textContent = slides.length;

      if (slides.length < 2 || index <= 0) {
        prevButton.setAttribute('disabled', '');
      } else {
        prevButton.removeAttribute('disabled');
      }

      if (slides.length < 2 || index >= slides.length - 1) {
        nextButton.setAttribute('disabled', '');
      } else {
        nextButton.removeAttribute('disabled');
      }
    }
    // TAQB END

    onSlideChanged(event) {
      if (event.detail.currentElement == undefined) return;

      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, scrollIntoView = true) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if ((!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') && scrollIntoView) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);

      // TAQB: sync slide counter / arrow disabled state when desktop arrows + thumbnails
      if (this.usesDesktopGalleryArrowNavigation()) {
        this.updateDesktopGalleryArrowControls();
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('[id^="Slide-"]').forEach((element) => {
        element.classList.remove('is-active');
        element.querySelector('button').removeAttribute('aria-current');
      });
      thumbnail.classList.add('is-active');
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = theme.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
